import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Backspace, SpinnerGap, LockKey } from "@phosphor-icons/react";

const MAX_PIN = 6;
const MIN_PIN = 3;
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

export default function Login() {
  const { login } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPin = async (code) => {
    setBusy(true);
    setError("");
    try {
      await login(code);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const press = (d) => {
    if (busy) return;
    setError("");
    setPin((p) => (p.length >= MAX_PIN ? p : p + d));
  };

  const back = () => {
    if (busy) return;
    setError("");
    setPin((p) => p.slice(0, -1));
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0A0C] flex">
      <div
        className="hidden lg:flex flex-1 relative border-r border-white/10"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/19317897/pexels-photo-19317897.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#0A0A0C]/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <ShieldCheck size={28} weight="fill" className="text-[#007AFF]" />
            <span className="font-display font-black text-xl tracking-tighter uppercase">SentryVision</span>
          </div>
          <div>
            <h1 className="font-display font-black text-5xl leading-none tracking-tighter uppercase max-w-md">
              AI Surveillance Command Center
            </h1>
            <p className="mt-4 text-[#A1A1AA] max-w-sm text-sm leading-relaxed">
              Real-time behavioral analytics for employee effectiveness, customer interaction quality, and theft & vandalism detection — from a single console.
            </p>
            <div className="mt-8 flex gap-6 font-mono-data text-xs text-[#71717A] uppercase tracking-widest">
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#32D74B] live-dot" /> 6 Feeds</span>
              <span>RTSP · ONVIF</span>
              <span>Gemini Vision</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[480px] flex items-center justify-center p-8">
        <div className="w-full max-w-xs" data-testid="login-form">
          <div className="flex items-center gap-2 lg:hidden mb-8">
            <ShieldCheck size={26} weight="fill" className="text-[#007AFF]" />
            <span className="font-display font-black text-lg tracking-tighter uppercase">SentryVision</span>
          </div>
          <p className="font-mono-data text-xs text-[#71717A] uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <LockKey size={13} weight="bold" /> Secure Access
          </p>
          <h2 className="font-display font-black text-3xl tracking-tighter uppercase mb-8">Enter Code</h2>

          <div className="flex justify-center gap-3 mb-6" data-testid="pin-display">
            {Array.from({ length: Math.max(MIN_PIN, pin.length) }).map((_, i) => (
              <div
                key={i}
                className={`w-3.5 h-3.5 rounded-full border transition-all duration-150 ${
                  i < pin.length
                    ? "bg-[#007AFF] border-[#007AFF] scale-110"
                    : "bg-transparent border-white/25"
                }`}
              />
            ))}
          </div>

          {error && (
            <div data-testid="login-error" className="mb-4 px-3 py-2.5 bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-sm font-mono-data text-center">
              {error}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {KEYS.map((k, idx) => {
              if (k === "") return <div key={idx} />;
              if (k === "back")
                return (
                  <button
                    key={idx}
                    type="button"
                    data-testid="pin-backspace"
                    onClick={back}
                    disabled={busy || pin.length === 0}
                    className="h-16 flex items-center justify-center bg-[#121214] border border-white/10 text-[#A1A1AA] hover:border-white/30 hover:text-white transition-colors disabled:opacity-40 active:scale-95"
                  >
                    <Backspace size={22} weight="bold" />
                  </button>
                );
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`pin-key-${k}`}
                  onClick={() => press(k)}
                  disabled={busy}
                  className="h-16 flex items-center justify-center bg-[#121214] border border-white/10 text-white font-mono-data text-2xl font-bold hover:border-[#007AFF] hover:bg-[#007AFF]/10 transition-colors disabled:opacity-40 active:scale-95"
                >
                  {k}
                </button>
              );
            })}
          </div>

          <button
            data-testid="pin-submit"
            onClick={() => pin.length >= MIN_PIN && submitPin(pin)}
            disabled={busy || pin.length < MIN_PIN}
            className="mt-3 w-full py-3 bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-30 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-wide text-sm transition-colors"
          >
            Enter
          </button>

          <div className="mt-6 h-5 flex items-center justify-center">
            {busy && (
              <span className="flex items-center gap-2 text-xs font-mono-data text-[#71717A] uppercase tracking-widest">
                <SpinnerGap size={15} className="animate-spin" /> Authenticating
              </span>
            )}
          </div>

          <p className="mt-2 text-xs font-mono-data text-[#71717A] text-center">
            Type your code, then press Enter. Master = 5 digits · Staff = 3-4 digits.
          </p>
          <p className="mt-6 text-center text-[9px] font-mono-data tracking-[0.25em] text-[#3f3f46] uppercase">Powered by A V Tech</p>
        </div>
      </div>
    </div>
  );
}
